import { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Youtube from '@tiptap/extension-youtube';
import { useInput, InputHelperText, Labeled } from 'react-admin';
import {
  Box, ToggleButton, ToggleButtonGroup, Tooltip, IconButton, Stack, Divider,
  Menu, MenuItem, ListItemIcon, ListItemText, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import TabIcon from '@mui/icons-material/Tab';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import GridOnIcon from '@mui/icons-material/GridOn';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { uploadFile } from '../providers/dataProvider.js';

// Image extension extended with a `width` attribute (rendered as an inline
// style) so admins can resize inserted images.
const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.style?.width || el.getAttribute('width') || null,
        renderHTML: (attrs) => (attrs.width ? { style: `width: ${attrs.width}` } : {}),
      },
      // Optional hyperlink. Tiptap's Link is a text mark and can't wrap a block
      // image, so the URL lives on the image node and is rendered as a
      // surrounding <a> (and read back off that <a> when parsing).
      href: {
        default: null,
        parseHTML: (el) => {
          const a = el.parentElement;
          return a && a.tagName === 'A' ? a.getAttribute('href') : null;
        },
        renderHTML: () => ({}), // emitted by the wrapper below, not on the <img>
      },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    const img = ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    const href = node.attrs.href;
    if (!href) return img;
    return ['a', { href, class: 'image-link', target: '_blank', rel: 'noopener noreferrer' }, img];
  },
});

// Table extended with a `borderless` flag (rendered as a `no-border` class)
// so admins can hide the grid lines for clean layout/image tables.
const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      borderless: {
        default: false,
        parseHTML: (el) => el.classList?.contains('no-border'),
        renderHTML: (attrs) => (attrs.borderless ? { class: 'no-border' } : {}),
      },
    };
  },
});

// Block node storing admin-defined content tabs: [{ label, url }, …].
// Saved as <div class="page-tabs" data-page-tabs="[…json…]"></div>; the public
// site hydrates it into tab buttons that load the linked page's content
// (via AJAX) when a visitor clicks. See web/src/lib/pageTabs.js.
const PageTabs = Node.create({
  name: 'pageTabs',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      tabs: {
        default: [],
        parseHTML: (el) => {
          try {
            const v = JSON.parse(el.getAttribute('data-page-tabs') || '[]');
            return Array.isArray(v) ? v : [];
          } catch { return []; }
        },
        renderHTML: (attrs) => ({ 'data-page-tabs': JSON.stringify(attrs.tabs || []) }),
      },
    };
  },
  parseHTML() { return [{ tag: 'div[data-page-tabs]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'page-tabs' })];
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'pt-tabs-node';
      const render = (n) => {
        dom.innerHTML = '';
        const badge = document.createElement('strong');
        badge.textContent = 'Content tabs:';
        dom.appendChild(badge);
        const tabs = n.attrs.tabs || [];
        if (!tabs.length) {
          const s = document.createElement('em');
          s.textContent = ' (no tabs yet)';
          dom.appendChild(s);
        }
        tabs.forEach((t) => {
          const chip = document.createElement('span');
          chip.className = 'chip';
          chip.textContent = t.label || '(untitled)';
          dom.appendChild(chip);
        });
        const hint = document.createElement('span');
        hint.className = 'hint';
        hint.textContent = 'select, then use the tabs toolbar button to edit';
        dom.appendChild(hint);
      };
      render(node);
      return {
        dom,
        update: (n) => { if (n.type.name !== 'pageTabs') return false; render(n); return true; },
      };
    };
  },
});

/**
 * Collapsible content ("accordion") — unlike PageTabs, the content of each
 * section is edited inline, right here in the body, and stored with the page
 * (no separate page slug). Rendered as:
 *   <div data-accordion>
 *     <div data-accordion-item data-label="…">…rich content…</div>
 *   </div>
 * The public site collapses every section and expands on click
 * (see web/src/lib/accordions.js).
 */
const AccordionItem = Node.create({
  name: 'accordionItem',
  content: 'block+',
  defining: true,
  addAttributes() {
    return {
      label: {
        default: 'Section',
        parseHTML: (el) => el.getAttribute('data-label') || 'Section',
        renderHTML: (attrs) => ({ 'data-label': attrs.label || '' }),
      },
    };
  },
  parseHTML() { return [{ tag: 'div[data-accordion-item]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-accordion-item': '' }), 0];
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('div');
      dom.className = 'acc-item';

      const head = document.createElement('div');
      head.className = 'acc-item-head';
      head.contentEditable = 'false';

      const input = document.createElement('input');
      input.className = 'acc-item-label';
      input.value = node.attrs.label || '';
      input.placeholder = 'Section label…';
      input.addEventListener('input', () => {
        if (typeof getPos !== 'function') return;
        editor.commands.command(({ tr }) => {
          tr.setNodeAttribute(getPos(), 'label', input.value);
          return true;
        });
      });

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'acc-item-del';
      del.textContent = '×';
      del.title = 'Remove this section';
      del.addEventListener('click', () => {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      });

      head.append(input, del);

      const body = document.createElement('div');
      body.className = 'acc-item-body';

      dom.append(head, body);

      return {
        dom,
        contentDOM: body,
        update: (updated) => {
          if (updated.type.name !== 'accordionItem') return false;
          if (document.activeElement !== input && input.value !== updated.attrs.label) {
            input.value = updated.attrs.label || '';
          }
          return true;
        },
        stopEvent: (event) => head.contains(event.target),
        ignoreMutation: (mutation) => !body.contains(mutation.target) || body === mutation.target,
      };
    };
  },
});

const Accordion = Node.create({
  name: 'accordion',
  group: 'block',
  content: 'accordionItem+',
  parseHTML() { return [{ tag: 'div[data-accordion]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-accordion': '', class: 'accordion' }), 0];
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('div');
      dom.className = 'acc-block';

      const cap = document.createElement('div');
      cap.className = 'acc-cap';
      cap.contentEditable = 'false';
      cap.textContent = 'Collapsible content';

      const items = document.createElement('div');
      items.className = 'acc-items';

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'acc-add';
      add.textContent = '+ Add section';
      add.contentEditable = 'false';
      add.addEventListener('click', () => {
        if (typeof getPos !== 'function') return;
        const endOfBlock = getPos() + node.nodeSize - 1;
        editor.chain().focus().insertContentAt(endOfBlock, {
          type: 'accordionItem',
          attrs: { label: 'New section' },
          content: [{ type: 'paragraph' }],
        }).run();
      });

      dom.append(cap, items, add);

      return {
        dom,
        contentDOM: items,
        update: (updated) => updated.type.name === 'accordion',
        stopEvent: (event) => event.target === add,
        ignoreMutation: (mutation) => !items.contains(mutation.target) || items === mutation.target,
      };
    };
  },
});

/** Dialog for adding/editing the tabs (label + page URL rows). */
function TabsDialog({ open, initial, onClose, onSave }) {
  const [rows, setRows] = useState([{ label: '', url: '' }]);
  useEffect(() => {
    if (open) setRows(initial && initial.length ? initial.map((r) => ({ ...r })) : [{ label: '', url: '' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const setRow = (i, p) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Content tabs</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each tab becomes a button on the page; clicking it loads the linked page's
          content below the buttons. Link to pages on this site — e.g. <code>/page/scholarship</code>.
        </Typography>
        <Stack spacing={1.5}>
          {rows.map((r, i) => (
            /* eslint-disable-next-line react/no-array-index-key */
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small" label="Tab label" value={r.label}
                onChange={(e) => setRow(i, { label: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small" label="Page URL (/page/slug)" value={r.url}
                onChange={(e) => setRow(i, { url: e.target.value })}
                sx={{ flex: 1.4 }}
              />
              <IconButton size="small" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} aria-label="Remove tab">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setRows([...rows, { label: '', url: '' }])} sx={{ mt: 1.5 }}>
          Add tab
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(
            rows
              .map((r) => ({ label: (r.label || '').trim(), url: (r.url || '').trim() }))
              .filter((r) => r.label && r.url),
          )}
        >
          Save tabs
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// School palette + common colours for the text-colour picker.
const TEXT_COLORS = [
  { label: 'Default', value: null },
  { label: 'Ink', value: '#22302a' },
  { label: 'Green', value: '#1b5e3f' },
  { label: 'Gold', value: '#c19a3a' },
  { label: 'Red', value: '#b54033' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Purple', value: '#7c3aed' },
  { label: 'Grey', value: '#5d6660' },
];

const IMAGE_WIDTHS = [
  { label: 'S', value: '25%' },
  { label: 'M', value: '50%' },
  { label: 'L', value: '75%' },
  { label: 'Full', value: '100%' },
];

/**
 * Rich-text input for Page.body, News.body, etc. Stores HTML as a string.
 * Supports headings, bold/italic/underline/strike, text colour + highlight,
 * alignment, lists, quotes, links, resizable images and tables.
 */
export function TiptapInput(props) {
  const { source, label, helperText, validate } = props;
  const { field, fieldState, isRequired } = useInput({ source, validate });
  const [colorAnchor, setColorAnchor] = useState(null);
  const [tableAnchor, setTableAnchor] = useState(null);
  const [tabsOpen, setTabsOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ResizableImage.configure({ HTMLAttributes: { class: 'tiptap-image' } }),
      Link.configure({ openOnClick: false, autolink: true }),
      CustomTable.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Youtube.configure({ controls: true, nocookie: true, modestBranding: true, HTMLAttributes: { class: 'yt-embed' } }),
      PageTabs,
      Accordion,
      AccordionItem,
    ],
    content: field.value || '',
    editorProps: { attributes: { class: 'tiptap-content' } },
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML();
      if (html !== field.value) field.onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = field.value || '';
    if (next !== current) editor.commands.setContent(next, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, field.value]);

  if (!editor) return null;

  const insertLink = () => {
    // With an image selected, link the image itself instead of any text.
    if (editor.isActive('image')) {
      const previous = editor.getAttributes('image')?.href || '';
      const url = window.prompt('Link URL for this image (clear the box to remove the link)', previous || 'https://');
      if (url === null) return;
      editor.chain().focus().updateAttributes('image', { href: url.trim() || null }).run();
      return;
    }
    const previous = editor.getAttributes('link')?.href;
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const insertAccordion = () => {
    editor.chain().focus().insertContent({
      type: 'accordion',
      content: [
        { type: 'accordionItem', attrs: { label: 'Section 1' }, content: [{ type: 'paragraph' }] },
        { type: 'accordionItem', attrs: { label: 'Section 2' }, content: [{ type: 'paragraph' }] },
      ],
    }).run();
  };

  const insertYoutube = () => {
    const url = window.prompt('Paste a YouTube video link', 'https://www.youtube.com/watch?v=');
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url.trim() }).run();
  };

  const insertImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const result = await uploadFile(file);
        editor.chain().focus().setImage({ src: result.url, alt: '' }).run();
      } catch (err) {
        alert(`Upload failed: ${err.message}`);
      }
    };
    input.click();
  };

  const setColor = (value) => {
    if (value) editor.chain().focus().setColor(value).run();
    else editor.chain().focus().unsetColor().run();
    setColorAnchor(null);
  };

  const alignment = ['left', 'center', 'right', 'justify'].find((a) => editor.isActive({ textAlign: a })) || 'left';
  const imageActive = editor.isActive('image');
  const inTable = editor.isActive('table');
  const tableBorderless = !!editor.getAttributes('table')?.borderless;
  const tabsActive = editor.isActive('pageTabs');
  const currentTabs = tabsActive ? (editor.getAttributes('pageTabs')?.tabs || []) : [];

  const saveTabs = (rows) => {
    setTabsOpen(false);
    if (!rows.length) {
      // Saving an empty list on an existing block removes it.
      if (tabsActive) editor.chain().focus().deleteSelection().run();
      return;
    }
    if (tabsActive) editor.chain().focus().updateAttributes('pageTabs', { tabs: rows }).run();
    else editor.chain().focus().insertContent({ type: 'pageTabs', attrs: { tabs: rows } }).run();
  };

  const tableAction = (fn) => { fn(); setTableAnchor(null); };

  return (
    <Labeled label={label || source} isRequired={isRequired} fullWidth>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, background: 'background.paper' }}>
          <Stack direction="row" spacing={0.5} alignItems="center"
            sx={{ p: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap', rowGap: 0.5 }}>

            {/* Headings */}
            <ToggleButtonGroup size="small" exclusive>
              <Tooltip title="Heading 2"><ToggleButton value="h2" selected={editor.isActive('heading', { level: 2 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToggleButton></Tooltip>
              <Tooltip title="Heading 3"><ToggleButton value="h3" selected={editor.isActive('heading', { level: 3 })}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToggleButton></Tooltip>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Inline styles */}
            <Tooltip title="Bold"><IconButton size="small" color={editor.isActive('bold') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Italic"><IconButton size="small" color={editor.isActive('italic') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Underline"><IconButton size="small" color={editor.isActive('underline') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Strikethrough"><IconButton size="small" color={editor.isActive('strike') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></IconButton></Tooltip>

            {/* Colour + highlight */}
            <Tooltip title="Text colour"><IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
              <FormatColorTextIcon fontSize="small" sx={{ color: editor.getAttributes('textStyle')?.color || 'inherit' }} /></IconButton></Tooltip>
            <Tooltip title="Highlight"><IconButton size="small" color={editor.isActive('highlight') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleHighlight().run()}><BorderColorIcon fontSize="small" /></IconButton></Tooltip>
            <Menu anchorEl={colorAnchor} open={!!colorAnchor} onClose={() => setColorAnchor(null)}>
              {TEXT_COLORS.map((c) => (
                <MenuItem key={c.label} onClick={() => setColor(c.value)} dense>
                  <ListItemIcon>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '1px solid #ccc',
                      background: c.value || 'transparent' }} />
                  </ListItemIcon>
                  <ListItemText>{c.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Alignment */}
            <ToggleButtonGroup size="small" exclusive value={alignment}>
              <Tooltip title="Align left"><ToggleButton value="left"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}><FormatAlignLeftIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Align centre"><ToggleButton value="center"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}><FormatAlignCenterIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Align right"><ToggleButton value="right"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}><FormatAlignRightIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Justify"><ToggleButton value="justify"
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}><FormatAlignJustifyIcon fontSize="small" /></ToggleButton></Tooltip>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Blocks */}
            <Tooltip title="Quote"><IconButton size="small" color={editor.isActive('blockquote') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}><FormatQuoteIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Bullet list"><IconButton size="small" color={editor.isActive('bulletList') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Numbered list"><IconButton size="small" color={editor.isActive('orderedList') ? 'primary' : 'default'}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></IconButton></Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Link + image + video */}
            <Tooltip title="Insert / edit link"><IconButton size="small" onClick={insertLink}><LinkIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Insert image"><IconButton size="small" onClick={insertImage}><ImageIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Embed YouTube video"><IconButton size="small" onClick={insertYoutube}><SmartDisplayIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title={tabsActive ? 'Edit content tabs' : 'Insert content tabs'}>
              <IconButton size="small" color={tabsActive ? 'primary' : 'default'} onClick={() => setTabsOpen(true)}>
                <TabIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert collapsible content (accordion)">
              <IconButton
                size="small"
                color={editor.isActive('accordion') ? 'primary' : 'default'}
                onClick={insertAccordion}
              >
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Image resize — only when an image is selected */}
            {imageActive && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>Size:</Typography>
                <ToggleButtonGroup size="small" exclusive value={editor.getAttributes('image')?.width || '100%'}>
                  {IMAGE_WIDTHS.map((w) => (
                    <Tooltip key={w.value} title={`Width ${w.value}`}>
                      <ToggleButton value={w.value} sx={{ px: 1, py: 0.25, fontSize: 11 }}
                        onClick={() => editor.chain().focus().updateAttributes('image', { width: w.value }).run()}>
                        {w.label}
                      </ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
                <Tooltip title={editor.getAttributes('image')?.href ? `Image links to: ${editor.getAttributes('image').href}` : 'Add a link to this image'}>
                  <IconButton
                    size="small"
                    color={editor.getAttributes('image')?.href ? 'primary' : 'default'}
                    onClick={insertLink}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Table */}
            <Tooltip title="Table">
              <IconButton size="small" color={inTable ? 'primary' : 'default'} onClick={(e) => setTableAnchor(e.currentTarget)}>
                <TableChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={tableAnchor} open={!!tableAnchor} onClose={() => setTableAnchor(null)}>
              <MenuItem dense onClick={() => tableAction(() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}>
                <ListItemIcon><GridOnIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Insert 3×3 table</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().addRowAfter().run())}>Add row below</MenuItem>
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().addColumnAfter().run())}>Add column right</MenuItem>
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().deleteRow().run())}>Delete row</MenuItem>
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().deleteColumn().run())}>Delete column</MenuItem>
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().toggleHeaderRow().run())}>Toggle header row</MenuItem>
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().updateAttributes('table', { borderless: !tableBorderless }).run())}>
                {tableBorderless ? 'Show borders' : 'Hide borders'}
              </MenuItem>
              <Divider />
              <MenuItem dense disabled={!inTable} onClick={() => tableAction(() => editor.chain().focus().deleteTable().run())} sx={{ color: 'error.main' }}>Delete table</MenuItem>
            </Menu>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Undo"><IconButton size="small" onClick={() => editor.chain().focus().undo().run()}><UndoIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Redo"><IconButton size="small" onClick={() => editor.chain().focus().redo().run()}><RedoIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>

          <Box
            sx={{
              p: 2,
              // The editable area itself is resizable — drag its bottom-right
              // handle to make the editor taller/shorter for a roomier view.
              '& .tiptap-content': { outline: 'none', minHeight: 300, resize: 'vertical', overflow: 'auto' },
              '& .tiptap-content p': { marginBlock: '0.6em' },
              '& .tiptap-content h2': { fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', marginBlock: '0.8em 0.4em' },
              '& .tiptap-content h3': { fontFamily: '"Playfair Display", serif', fontSize: '1.25rem', marginBlock: '0.8em 0.4em' },
              '& .tiptap-content blockquote': { borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2, color: 'text.secondary', fontStyle: 'italic' },
              '& .tiptap-content img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
              '& .tiptap-content img.ProseMirror-selectednode': { outline: '2px solid', outlineColor: 'primary.main' },
              // Linked images get a subtle gold frame so it's obvious they carry a link.
              '& .tiptap-content a img': { outline: '2px solid #c19a3a', outlineOffset: '2px' },
              '& .tiptap-content [data-youtube-video]': { my: 1.5 },
              '& .tiptap-content iframe': { width: '100%', aspectRatio: '16 / 9', height: 'auto', border: 0, borderRadius: 1, display: 'block' },
              '& .tiptap-content a': { color: 'primary.main', textDecoration: 'underline' },
              '& .tiptap-content mark': { backgroundColor: '#fde68a', padding: '0 2px', borderRadius: '2px' },
              '& .tiptap-content table': { borderCollapse: 'collapse', width: '100%', margin: '1em 0', tableLayout: 'fixed', overflow: 'hidden' },
              '& .tiptap-content td, & .tiptap-content th': { border: '1px solid #d8d2c2', padding: '6px 10px', verticalAlign: 'top', position: 'relative', minWidth: '2em' },
              '& .tiptap-content th': { background: '#f3f0e6', fontWeight: 600, textAlign: 'left' },
              // Borderless tables: faint dashed guides in the editor only (so cells stay visible while editing).
              '& .tiptap-content table.no-border td, & .tiptap-content table.no-border th': { borderStyle: 'dashed', borderColor: '#e6e1d4' },
              '& .tiptap-content table.no-border th': { background: 'transparent' },
              // Content-tabs block placeholder (the real tabs render on the public site).
              '& .tiptap-content .pt-tabs-node': {
                border: '1px dashed #c19a3a', borderRadius: 1, p: 1.25, my: 1, background: '#fdf9ef',
                fontSize: 13, color: '#5d6660', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75,
                cursor: 'pointer',
              },
              '& .tiptap-content .pt-tabs-node .chip': { background: '#1d4d3a', color: '#fff', borderRadius: 99, px: 1.25, py: 0.25, fontSize: 12 },
              '& .tiptap-content .pt-tabs-node .hint': { fontSize: 11, fontStyle: 'italic', opacity: 0.7 },
              '& .tiptap-content .pt-tabs-node.ProseMirror-selectednode': { outline: '2px solid', outlineColor: 'primary.main' },
              // Collapsible content block — sections are edited inline.
              '& .tiptap-content .acc-block': { border: '1px dashed #1d4d3a', borderRadius: 1, p: 1.25, my: 1.5, background: '#f7faf8' },
              '& .tiptap-content .acc-cap': { fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#1d4d3a', mb: 1 },
              '& .tiptap-content .acc-item': { border: '1px solid #e6e1d4', borderRadius: 1, background: '#fff', mb: 1 },
              '& .tiptap-content .acc-item-head': { display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderBottom: '1px solid #f0ece0', background: '#fbf9f3' },
              '& .tiptap-content .acc-item-label': {
                flex: 1, border: '1px solid transparent', borderRadius: 1, px: 1, py: 0.5,
                font: 'inherit', fontWeight: 600, fontSize: 14, color: '#14181a', background: 'transparent',
                '&:hover': { borderColor: '#e6e1d4' },
                '&:focus': { outline: 'none', borderColor: '#c19a3a', background: '#fff' },
              },
              '& .tiptap-content .acc-item-del': {
                border: 0, background: 'transparent', cursor: 'pointer', color: '#8a948c',
                fontSize: 18, lineHeight: 1, px: 0.75, borderRadius: 1,
                '&:hover': { color: '#b54033', background: '#fbeae7' },
              },
              '& .tiptap-content .acc-item-body': { p: 1.25 },
              '& .tiptap-content .acc-item-body > *:first-of-type': { marginTop: 0 },
              '& .tiptap-content .acc-add': {
                border: '1px dashed #c19a3a', background: 'transparent', color: '#9a7818',
                cursor: 'pointer', fontSize: 12, fontWeight: 700, borderRadius: 1, px: 1.5, py: 0.5,
                '&:hover': { background: '#fdf6e6' },
              },
              '& .tiptap-content .selectedCell:after': { content: '""', position: 'absolute', inset: 0, background: 'rgba(193,154,58,0.18)', pointerEvents: 'none' },
              '& .tiptap-content .column-resize-handle': { position: 'absolute', right: '-2px', top: 0, bottom: 0, width: '4px', background: '#c19a3a', cursor: 'col-resize' },
            }}
          >
            <EditorContent editor={editor} />
          </Box>
        </Box>

        <InputHelperText touched={fieldState.isTouched} error={fieldState.error?.message} helperText={helperText} />

        <TabsDialog
          open={tabsOpen}
          initial={currentTabs}
          onClose={() => setTabsOpen(false)}
          onSave={saveTabs}
        />
      </Box>
    </Labeled>
  );
}
